import { z } from "zod"

/* The client's copy of the bell's responses.
 *
 * Written twice, once here and once in apps/api, as every response in this API
 * is. A field added to one and not the other is dropped by the client with no
 * error, so a test walks both shapes field path by field path.
 *
 * Generic on purpose: the bell renders sentences the registry in
 * packages/domain/src/notifications already wrote. */

export const ApiNotificationResponseSchema = z.object({
  notificationId: z.string().uuid(),
  /* Any string, not the registry's kinds: a client older than a new kind must
     still be able to render every other notice. */
  kind: z.string(),
  /* Also any string, for the same reason. The bell treats an unknown value
     as info. */
  severity: z.string(),
  /** Already-written sentences. The bell renders text, it does not assemble it. */
  title: z.string(),
  body: z.string(),
  linkPath: z.string().nullable(),
  /** The workspace the link belongs to. The bell switches to it first. */
  workspaceId: z.string().uuid().nullable(),
  /** How many events this row holds, for a burst folded into one row. */
  count: z.number().int().positive(),
  readAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  /** When the last event in this row happened. The bell sorts and groups by it. */
  occurredAt: z.string().datetime(),
})

export type ApiNotificationResponse = z.infer<
  typeof ApiNotificationResponseSchema
>

export const ApiNotificationListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
})

export const ApiNotificationListResponseSchema = z.object({
  /** Undismissed notices from the last 90 days, unread first, then newest. */
  notifications: z.array(ApiNotificationResponseSchema),
  /** Counted for this person, not this organisation. */
  unreadCount: z.number().int().nonnegative(),
})

export type ApiNotificationListResponse = z.infer<
  typeof ApiNotificationListResponseSchema
>

export const ApiMarkNotificationsReadBodySchema = z.object({
  /** One notice, or every unread one when absent. */
  notificationId: z.string().uuid().optional(),
})

export type ApiMarkNotificationsReadBody = z.infer<
  typeof ApiMarkNotificationsReadBodySchema
>

export const ApiMarkNotificationsReadResponseSchema = z.object({
  markedCount: z.number().int().nonnegative(),
  unreadCount: z.number().int().nonnegative(),
})

export type ApiMarkNotificationsReadResponse = z.infer<
  typeof ApiMarkNotificationsReadResponseSchema
>

export const ApiDismissNotificationsBodySchema = z.object({
  /** One notice, or every notice in the bell when absent. */
  notificationId: z.string().uuid().optional(),
})

export type ApiDismissNotificationsBody = z.infer<
  typeof ApiDismissNotificationsBodySchema
>

export const ApiDismissNotificationsResponseSchema = z.object({
  dismissedCount: z.number().int().nonnegative(),
  unreadCount: z.number().int().nonnegative(),
})

export type ApiDismissNotificationsResponse = z.infer<
  typeof ApiDismissNotificationsResponseSchema
>
